import {FC, useEffect, useState} from 'react'
import {api, ApiError, EmailTemplate, WelcomeEmail, WelcomeAttachment} from '../../services/api'
import {Spinner} from '../../components/Spinner'
import {PageHead} from '../../components/PageHead'
import {useToast} from '../../components/Toast'

const ACCENT = '#039540'
const INK = '#1b1b29'
const MUTED = '#7e8299'

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '11px 13px', border: '1px solid #e5e7eb', borderRadius: 9,
  fontSize: 14, background: '#fff', boxSizing: 'border-box', outline: 'none',
}
const cardStyle: React.CSSProperties = {
  background: '#fff', borderRadius: 16, border: '1px solid #eef0f4', padding: '22px 24px',
  marginBottom: 18, boxShadow: '0 4px 12px rgba(27,27,41,0.04)',
}
const Label: FC<{children: React.ReactNode}> = ({children}) => (
  <label style={{display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6}}>{children}</label>
)
const Toggle: FC<{on: boolean; onChange: (v: boolean) => void}> = ({on, onChange}) => (
  <button type='button' onClick={() => onChange(!on)} style={{display: 'inline-flex', alignItems: 'center', gap: 9, background: 'none', border: 'none', cursor: 'pointer', padding: 0}}>
    <span style={{width: 42, height: 24, borderRadius: 999, background: on ? ACCENT : '#cbd5e1', position: 'relative'}}>
      <span style={{position: 'absolute', top: 2, left: on ? 20 : 2, width: 20, height: 20, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)'}} />
    </span>
    <span style={{fontSize: 13, fontWeight: 700, color: on ? ACCENT : MUTED}}>{on ? 'ON' : 'OFF'}</span>
  </button>
)
const Placeholders: FC<{names: string[]}> = ({names}) => (
  <p style={{fontSize: 12.5, color: MUTED, margin: '4px 0 0', lineHeight: 1.7}}>
    Placeholders you can use anywhere above:{' '}
    {names.map((n, i) => (
      <span key={n}>{i > 0 && ', '}<code style={{background: '#f3f4f6', padding: '1px 5px', borderRadius: 4}}>{`{${n}}`}</code></span>
    ))}
  </p>
)

/** One of the generic renter-journey templates (tabs 1–4). */
const TemplateEditor: FC<{tpl: EmailTemplate; onSaved: (t: EmailTemplate) => void}> = ({tpl, onSaved}) => {
  const toast = useToast()
  const [s, setS] = useState<EmailTemplate>(tpl)
  const [saving, setSaving] = useState(false)
  useEffect(() => setS(tpl), [tpl])
  const set = (patch: Partial<EmailTemplate>) => setS((cur) => ({...cur, ...patch}))

  const save = async () => {
    setSaving(true)
    try {
      const saved = await api.updateEmailTemplate(s.key, {enabled: s.enabled, subject: s.subject, heading: s.heading, body: s.body})
      onSaved(saved)
      toast.success('Email saved.')
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Could not save')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className='ccp-stagger' style={{maxWidth: 760}}>
      <div className='ccp-lift' style={cardStyle}>
        <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4}}>
          <div style={{fontSize: 15, fontWeight: 700, color: INK}}>Send this email</div>
          <Toggle on={s.enabled} onChange={(v) => set({enabled: v})} />
        </div>
        <p style={{fontSize: 13, color: MUTED, margin: '0 0 18px'}}>{s.description}</p>

        <div style={{marginBottom: 14}}>
          <Label>Subject</Label>
          <input style={inputStyle} value={s.subject} onChange={(e) => set({subject: e.target.value})} />
        </div>
        <div style={{marginBottom: 14}}>
          <Label>Heading (big title inside the email)</Label>
          <input style={inputStyle} value={s.heading} onChange={(e) => set({heading: e.target.value})} />
        </div>
        <div style={{marginBottom: 6}}>
          <Label>Body</Label>
          <textarea style={{...inputStyle, minHeight: 200, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6}} value={s.body} onChange={(e) => set({body: e.target.value})} />
        </div>
        <Placeholders names={s.placeholders} />
      </div>
      <button onClick={save} disabled={saving} style={{fontWeight: 700, fontSize: 15, color: '#fff', background: ACCENT, border: 'none', borderRadius: 10, padding: '12px 26px', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1}}>
        {saving ? <Spinner size={14} /> : 'Save email'}
      </button>
    </div>
  )
}

/** The Welcome Packet (tab 5) — subject/heading/body + attachments. */
const WelcomePacketEditor: FC<{value: WelcomeEmail; onSaved: (w: WelcomeEmail) => void}> = ({value, onSaved}) => {
  const toast = useToast()
  const [s, setS] = useState<WelcomeEmail>(value)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  useEffect(() => setS(value), [value])
  const set = (patch: Partial<WelcomeEmail>) => setS((cur) => ({...cur, ...patch}))

  const addFile = async (file?: File) => {
    if (!file) return
    setUploading(true)
    try {
      const stored = await api.uploadFile(file)
      const att: WelcomeAttachment = {key: stored.key, filename: file.name}
      set({attachments: [...s.attachments, att]})
      toast.success('Attachment added — remember to save.')
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Upload failed')
    } finally {
      setUploading(false)
    }
  }
  const removeFile = (i: number) => set({attachments: s.attachments.filter((_, idx) => idx !== i)})

  // External links (a walkthrough video, a map) — shown as buttons in the
  // packet beside the document links. Files live in storage; these don't.
  const links = s.links ?? []
  const addLink = () => set({links: [...links, {label: '', url: ''}]})
  const setLink = (i: number, patch: Partial<{label: string; url: string}>) =>
    set({links: links.map((l, idx) => (idx === i ? {...l, ...patch} : l))})
  const removeLink = (i: number) => set({links: links.filter((_, idx) => idx !== i)})
  // Uploaded filenames carry typos and version numbers; the renter sees this instead.
  const setTitle = (i: number, title: string) =>
    set({attachments: s.attachments.map((a, idx) => (idx === i ? {...a, title} : a))})

  const save = async () => {
    setSaving(true)
    try {
      const saved = await api.updateWelcomeEmail({enabled: s.enabled, subject: s.subject, heading: s.heading, body: s.body, attachments: s.attachments, links: s.links ?? []})
      onSaved(saved)
      toast.success('Welcome packet saved.')
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Could not save')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className='ccp-stagger' style={{maxWidth: 760}}>
      <div className='ccp-lift' style={cardStyle}>
        <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4}}>
          <div style={{fontSize: 15, fontWeight: 700, color: INK}}>Send this email</div>
          <Toggle on={s.enabled} onChange={(v) => set({enabled: v})} />
        </div>
        <p style={{fontSize: 13, color: MUTED, margin: '0 0 18px'}}>Sent once a renter is approved and the deposit is received.</p>

        <div style={{marginBottom: 14}}>
          <Label>Subject</Label>
          <input style={inputStyle} value={s.subject} onChange={(e) => set({subject: e.target.value})} />
        </div>
        <div style={{marginBottom: 14}}>
          <Label>Heading (big title inside the email)</Label>
          <input style={inputStyle} value={s.heading} onChange={(e) => set({heading: e.target.value})} />
        </div>
        <div style={{marginBottom: 6}}>
          <Label>Body</Label>
          <textarea style={{...inputStyle, minHeight: 200, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6}} value={s.body} onChange={(e) => set({body: e.target.value})} />
        </div>
        <Placeholders names={['name', 'bedLabel', 'keyFob', 'moveIn']} />
      </div>

      <div className='ccp-lift' style={cardStyle}>
        <div style={{fontSize: 15, fontWeight: 700, color: INK, marginBottom: 4}}>Attachments</div>
        <p style={{fontSize: 13, color: MUTED, margin: '0 0 14px'}}>PDFs or images attached to every welcome email (house guide, welcome packet, etc.).</p>
        {s.attachments.length === 0 && <div style={{fontSize: 13, color: MUTED, marginBottom: 12}}>No attachments yet.</div>}
        {s.attachments.map((a, i) => (
          <div key={i} style={{padding: '10px 12px', border: '1px solid #eef0f4', borderRadius: 10, marginBottom: 8}}>
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10}}>
              <span style={{fontSize: 13, color: MUTED, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>📎 {a.filename}</span>
              <button onClick={() => removeFile(i)} style={{fontSize: 13, fontWeight: 600, color: '#d9214e', background: 'rgba(217,33,78,0.08)', border: 'none', borderRadius: 8, padding: '7px 12px', cursor: 'pointer'}}>Remove</button>
            </div>
            <input
              value={a.title ?? ''}
              onChange={(e) => setTitle(i, e.target.value)}
              placeholder='Button title the renter sees, e.g. Check List & Road Directions (1 of 2)'
              style={{...inputStyle, marginTop: 8}}
            />
          </div>
        ))}
        <label style={{display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 14, color: INK, background: '#fff', border: '1px dashed #cbd5e1', borderRadius: 10, padding: '10px 16px', cursor: uploading ? 'not-allowed' : 'pointer', opacity: uploading ? 0.7 : 1, marginTop: 4}}>
          {uploading ? <Spinner size={13} /> : '＋'} Add attachment
          <input type='file' accept='.pdf,image/*' hidden disabled={uploading} onChange={(e) => addFile(e.target.files?.[0])} />
        </label>
      </div>

      <div style={{marginTop: 26}}>
        <h3 style={{fontSize: 15, fontWeight: 800, color: INK, margin: '0 0 4px'}}>Links</h3>
        <p style={{fontSize: 13, color: MUTED, margin: '0 0 14px'}}>
          Anything hosted elsewhere — a walkthrough video, a map. Shown as buttons in the packet
          beside the documents. Make sure the link is viewable by anyone who has it.
        </p>
        {links.length === 0 && <div style={{fontSize: 13, color: MUTED, marginBottom: 12}}>No links yet.</div>}
        {links.map((l, i) => (
          <div key={i} style={{padding: '10px 12px', border: '1px solid #eef0f4', borderRadius: 10, marginBottom: 8}}>
            <div style={{display: 'flex', gap: 10, alignItems: 'center'}}>
              <input
                value={l.label}
                onChange={(e) => setLink(i, {label: e.target.value})}
                placeholder='Button title, e.g. House walkthrough video'
                style={{...inputStyle, flex: 1}}
              />
              <button
                onClick={() => removeLink(i)}
                style={{fontSize: 13, fontWeight: 600, color: '#d9214e', background: 'rgba(217,33,78,0.08)', border: 'none', borderRadius: 8, padding: '7px 12px', cursor: 'pointer'}}
              >
                Remove
              </button>
            </div>
            <input
              value={l.url}
              onChange={(e) => setLink(i, {url: e.target.value})}
              placeholder='https://…'
              style={{...inputStyle, marginTop: 8}}
            />
          </div>
        ))}
        <button
          onClick={addLink}
          style={{display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 14, color: INK, background: '#fff', border: '1px dashed #cbd5e1', borderRadius: 10, padding: '10px 16px', cursor: 'pointer', marginTop: 4}}
        >
          ＋ Add link
        </button>
      </div>

      <button onClick={save} disabled={saving} style={{fontWeight: 700, fontSize: 15, color: '#fff', background: ACCENT, border: 'none', borderRadius: 10, padding: '12px 26px', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1}}>
        {saving ? <Spinner size={14} /> : 'Save welcome packet'}
      </button>
    </div>
  )
}

/** Admin editor for every renter-journey email — one tab per step (1–5). */
const WelcomeEmailPage: FC = () => {
  const [templates, setTemplates] = useState<EmailTemplate[] | null>(null)
  const [welcome, setWelcome] = useState<WelcomeEmail | null>(null)
  const [active, setActive] = useState(0)
  const [loadErr, setLoadErr] = useState<string | null>(null)

  useEffect(() => {
    Promise.all([api.getEmailTemplates(), api.getWelcomeEmail()])
      .then(([t, w]) => {
        setTemplates(t)
        setWelcome(w)
      })
      .catch((e) => setLoadErr(e instanceof ApiError ? e.message : 'Could not load'))
  }, [])

  const head = <PageHead icon='✉️' eyebrow='Content' title='Emails' sub='Edit every automated email a renter receives — one tab per step of the journey.' />

  if (loadErr) return <div>{head}<div style={{color: '#dc2626'}}>{loadErr}</div></div>
  if (!templates || !welcome) return <div>{head}<Spinner size={20} /></div>

  const tabs = [
    ...templates.map((t) => ({label: t.title, key: t.key})),
    {label: '5 · Welcome Packet', key: 'welcome_packet'},
  ]

  return (
    <div>
      {head}
      <div style={{display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 22, borderBottom: '1px solid #eef0f4', paddingBottom: 2}}>
        {tabs.map((t, i) => (
          <button
            key={t.key}
            onClick={() => setActive(i)}
            style={{
              fontWeight: 700, fontSize: 13.5, padding: '9px 16px', borderRadius: '10px 10px 0 0',
              border: 'none', cursor: 'pointer',
              color: active === i ? ACCENT : MUTED,
              background: active === i ? 'rgba(3,149,64,0.08)' : 'transparent',
              borderBottom: active === i ? `2px solid ${ACCENT}` : '2px solid transparent',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {active < templates.length ? (
        <TemplateEditor
          tpl={templates[active]}
          onSaved={(saved) => setTemplates((cur) => (cur ? cur.map((x) => (x.key === saved.key ? saved : x)) : cur))}
        />
      ) : (
        <WelcomePacketEditor value={welcome} onSaved={setWelcome} />
      )}
    </div>
  )
}

export {WelcomeEmailPage}
